import type { FiatFlow, UseFiatQuoteResult } from '@funkit/connect-core'
import { isStaleQuoteFailure } from '@funkit/connect-core'
import { useEffect, useState } from 'react'

import { logger } from '../logging/logger'

type RecoverableFlow = Pick<FiatFlow, 'phase' | 'submitError' | 'stepResponse'>

type RecoverableQuote = Pick<
  UseFiatQuoteResult,
  'quote' | 'isQuoteFresh' | 'quoteError' | 'retryQuote' | 'submitQuote'
>

export interface StaleQuoteRecovery {
  /** A replacement price is on its way, so the entry screen holds. */
  isReplacing: boolean
  /** Asks for another replacement — what a retry means on a dead ref. */
  replaceQuote: () => void
}

/**
 * Re-sends an entry submission the server answered with a dead quoteRef.
 *
 * - The ref is the server's, carried in the transition it hands back, so a
 *   replay cannot fix it. A new price brings a new transition naming a live
 *   one, and what the user typed rides on the flow rather than the round.
 * - Automatic once, then only when asked. A provider that declined answers
 *   with the same 404, so a self-driven loop never reaches a different answer.
 * - Only while the client owns the quote. Once the flow holds a round the
 *   query is off, so there is no replacement to wait for, and the way to a new
 *   price is giving the round up.
 */
export function useStaleQuoteRecovery(
  flow: RecoverableFlow,
  quote: RecoverableQuote,
  {
    hold = false,
  }: {
    /**
     * The failed send followed a completed capture, so a replacement price
     * would open a second sheet on a card already charged. Nothing is replaced
     * while this holds.
     */
    hold?: boolean
  } = {},
): StaleQuoteRecovery {
  /** The ref being replaced, which is also what says a recovery is running. */
  const [deadQuoteRef, setDeadQuoteRef] = useState<string>()
  const [isArmed, setIsArmed] = useState(true)
  /** The query has left the price it was holding, so what it shows now is new. */
  const [hasQuoteMoved, setHasQuoteMoved] = useState(false)

  const isEntryDeadRef =
    !hold &&
    flow.stepResponse === undefined &&
    flow.phase === 'RETRYABLE' &&
    isStaleQuoteFailure(flow.submitError)
  const quoteRef = quote.quote?.quoteRef

  const { retryQuote, submitQuote, isQuoteFresh, quoteError } = quote

  useEffect(() => {
    if (!isEntryDeadRef || !isArmed || deadQuoteRef !== undefined) {
      return
    }
    setIsArmed(false)
    setHasQuoteMoved(false)
    logger.warn('connect-rn:fiat:entry_quote_ref_dead')
    setDeadQuoteRef(quoteRef ?? '')
    retryQuote()
  }, [isEntryDeadRef, isArmed, quoteRef, deadQuoteRef, retryQuote])

  // The query still reports the dead price as fresh until its replacement
  // starts loading, so waiting on the ref alone would read the old answer as
  // the new one.
  useEffect(() => {
    if (deadQuoteRef !== undefined && !isQuoteFresh) {
      setHasQuoteMoved(true)
    }
  }, [deadQuoteRef, isQuoteFresh])

  useEffect(() => {
    if (deadQuoteRef === undefined) {
      return
    }
    // Nothing is coming to re-send under, so the failure screen should have
    // the user rather than a spinner that never resolves.
    if (quoteError !== undefined) {
      logger.warn('connect-rn:fiat:entry_quote_replacement_failed')
      setDeadQuoteRef(undefined)
      return
    }
    if (!hasQuoteMoved || !isQuoteFresh) {
      return
    }
    setDeadQuoteRef(undefined)
    // Every quote is minted its own ref, so the same one back is the server
    // contradicting itself. Give the user the retry rather than wait on a
    // replacement that has already arrived.
    // A replacement may be a server-owned round (for example SESSION_AUTH or
    // KYC) rather than another QUOTE. In that case `quote` is intentionally
    // absent, but the completed refetch is still the authoritative next step
    // and must be handed to the flow. Only reject an actual quote that repeats
    // the dead ref.
    if (quoteRef === deadQuoteRef) {
      logger.warn('connect-rn:fiat:entry_quote_not_replaced')
      return
    }
    logger.info('connect-rn:fiat:entry_resent_on_new_quote')
    submitQuote()
  }, [
    deadQuoteRef,
    quoteRef,
    hasQuoteMoved,
    isQuoteFresh,
    quoteError,
    submitQuote,
  ])

  return {
    isReplacing: deadQuoteRef !== undefined,
    replaceQuote: () => setIsArmed(true),
  }
}
